import find from 'lodash/find';
import compact from 'lodash/compact';
import flatten from 'lodash/flatten';
import uniq from 'lodash/uniq';

export default class Chords {
  static formatChords(chords, limit = 3) {
    let chordsArray = [];
    chords.forEach(chord => {
      chordsArray.push(chord.name.charAt(0));
    });
    chordsArray = chordsArray.filter((item, index, array) => array.indexOf(item) === index);
    if (chordsArray.length > limit) {
      chordsArray.splice(limit);
      chordsArray.push('...');
    }
    return chordsArray;
  }

  static formatLessonsForChordDisplay(lessons, overlayColor = 'blue') {
    if (!lessons) {
      return [];
    }
    const formattedLessons = Object.keys(lessons).map(key => {
      const lesson = lessons[key];
      const chordArr = Chords.getChordsFromLesson(lesson);
      if (!chordArr || !chordArr.length) {
        return lesson;
      }
      const overlayTitle = chordArr[0].name;
      const { title } = chordArr[0];
      return {
        ...lesson,
        overlayTitle,
        overlayColor,
        title,
      };
    });
    return formattedLessons;
  }

  static getChordsFromLesson(lesson) {
    if (
      lesson &&
      lesson['activity-lesson-video'] &&
      lesson['activity-lesson-video'].chords &&
      lesson['activity-lesson-video'].chords.length
    ) {
      return lesson['activity-lesson-video'].chords;
    }
    if (lesson && lesson.chords) {
      return lesson.chords;
    }
    return null;
  }

  static getChordNamesInCourse(course) {
    if (course && course.lessons && course.lessons.length) {
      const chordObjArr = course.lessons.map(lesson => Chords.getChordsFromLesson(lesson));
      const compactChordArr = compact(chordObjArr);
      if (!compactChordArr || !compactChordArr.length) {
        return null;
      }
      const chordNameArr = flatten(compactChordArr).map(chordObj => chordObj.name);
      return uniq(chordNameArr).sort().join(' ');
    }
    return null;
  }

  static parseChordSequences(chords, timedAnnotations, allChordsStr) {
    if (!chords) {
      return [];
    }
    const chordSequences = [
      {
        title: timedAnnotations && timedAnnotations.length ? allChordsStr : null,
        chords,
        boldTitle: true,
      },
    ];
    const blankChord = {
      id: 'NO_MATCH',
      title: '',
      name: '',
      shape: 'x-x-x-x-x-x',
      fingers: '111111',
    };
    if (!timedAnnotations) {
      return chordSequences;
    }
    timedAnnotations.forEach(annotation => {
      const { caption } = annotation;
      const chordStrings = caption
        .replace(/\|/g, '')
        .replace(/:/g, '')
        .replace(/\s+/g, ' ')
        .replace(/\(/g, '')
        .replace(/\)/g, '')
        .replace(' DD', '-DD')
        .trim();
      const chordArr = [];
      chordStrings.split(' ').forEach(str => {
        const checkedStr = str.indexOf('-DD') > -1 ? str.replace('-DD', ' DD') : str;
        const matchChord = find(chords, obj => obj.name.trim() === checkedStr);
        // verified using this site: https://regex101.com/
        if (checkedStr.match(/^[a-gA-G][0-9|#|a-zA-Z|-|/|\s+DD]*$/)) {
          if (matchChord) {
            chordArr.push(matchChord);
          } else {
            chordArr.push({
              ...blankChord,
              title: checkedStr,
              name: checkedStr,
            });
          }
        }
      });
      chordSequences.push({
        ...annotation,
        chords: chordArr,
      });
    });

    return chordSequences;
  }

  static parseLessonChordSequences(currentLesson, allChordsStr) {
    if (!currentLesson['activity-lesson-video'] || !currentLesson['activity-lesson-video'].chords) {
      return [];
    }
    const activityLessonVideo = currentLesson['activity-lesson-video'];
    const { chords } = activityLessonVideo;
    const timedAnnotations = activityLessonVideo['timed-annotations'] || [];

    return Chords.parseChordSequences(chords, timedAnnotations, allChordsStr);
  }

  static parseChordsToFrames(chords) {
    if (!chords) {
      return [];
    }
    const chordArr = Array.isArray(chords) ? chords : [chords];
    return chordArr.map(chord => {
      let highestNote = 0;
      let lowestNote = 22;
      const notes = chord.shape
        .split('-')
        .reverse()
        .map(note => {
          if (note === 'x' || Object.is(note, NaN)) {
            return null;
          }
          const numberNote = Number(note);
          if (numberNote > highestNote) {
            highestNote = numberNote;
          }
          if (numberNote !== 0 && numberNote < lowestNote) {
            lowestNote = numberNote;
          }
          return numberNote;
        });

      const fingers = chord.fingers
        .split('')
        .reverse()
        .map(finger => {
          if (finger === 'x' || finger === 'o') return null;
          if (finger === 't') return 'T';
          const fingerNumber = Number(finger);
          return fingerNumber;
        });

      const fretWidth = Math.max(4, highestNote - lowestNote + 1);
      const firstFret = highestNote > fretWidth ? lowestNote : 1;
      return {
        frame: {
          firstFret,
          fretWidth,
          label: chord.name,
          notes,
          fingers,
        },
      };
    });
  }

  static groupChords(lessons) {
    const chordGroups = {
      A: [],
      'A# (Bb)': [],
      B: [],
      C: [],
      'C# (Db)': [],
      D: [],
      'D# (Eb)': [],
      E: [],
      F: [],
      'F# (Gb)': [],
      G: [],
      'G# (Ab)': [],
    };
    Object.keys(lessons).forEach(key => {
      const lesson = lessons[key];
      const chords = Chords.getChordsFromLesson(lesson);
      if (chords) {
        const chord = chords[0];

        if (chord.name.match(/^A#/) || chord.name.match(/^Bb/)) {
          chordGroups['A# (Bb)'].push(lesson);
        } else if (chord.name.match(/^Db/) || chord.name.match(/^C#/)) {
          chordGroups['C# (Db)'].push(lesson);
        } else if (chord.name.match(/^Eb/) || chord.name.match(/^D#/)) {
          chordGroups['D# (Eb)'].push(lesson);
        } else if (chord.name.match(/^Gb/) || chord.name.match(/^F#/)) {
          chordGroups['F# (Gb)'].push(lesson);
        } else if (chord.name.match(/^Ab/) || chord.name.match(/^G#/)) {
          chordGroups['G# (Ab)'].push(lesson);
        } else if (chord.name.match(/^A/)) {
          chordGroups.A.push(lesson);
        } else if (chord.name.match(/^B/)) {
          chordGroups.B.push(lesson);
        } else if (chord.name.match(/^C/)) {
          chordGroups.C.push(lesson);
        } else if (chord.name.match(/^D/)) {
          chordGroups.D.push(lesson);
        } else if (chord.name.match(/^E/)) {
          chordGroups.E.push(lesson);
        } else if (chord.name.match(/^F/)) {
          chordGroups.F.push(lesson);
        } else if (chord.name.match(/^G/)) {
          chordGroups.G.push(lesson);
        }
      }
    });
    const omittedGroups = {};
    Object.keys(chordGroups).forEach(key => {
      if (chordGroups[key].length) {
        omittedGroups[key] = chordGroups[key];
      }
    });
    return omittedGroups;
  }
}
